// Body map — muscle-group taxonomy + display metadata. Kept identical between
// apps/mobile/src/shared/bodyMap.ts and src/lib/bodyMap.ts so labels/colors match.
// Muscle vector artwork & slugs derive from react-native-body-highlighter (MIT).
// The SVG scene (assets/bodymap/index.html) tags each muscle path with these ids.

export const BODY_ZONES = [
  'chest_l', 'chest_r', 'obliques_l', 'obliques_r', 'abs_l', 'abs_r',
  'biceps_l', 'biceps_r', 'triceps_l', 'triceps_r', 'neck', 'trapezius_l',
  'trapezius_r', 'deltoids_l', 'deltoids_r', 'adductors_l', 'adductors_r', 'quadriceps_l',
  'quadriceps_r', 'knees_l', 'knees_r', 'tibialis_l', 'tibialis_r', 'calves_l',
  'calves_r', 'forearm_l', 'forearm_r', 'hands_l', 'hands_r', 'ankles_l',
  'ankles_r', 'feet_l', 'feet_r', 'head', 'upper_back_l', 'upper_back_r',
  'lower_back_l', 'lower_back_r', 'gluteal_l', 'gluteal_r', 'hamstring_l', 'hamstring_r',
] as const;

export type BodyZoneId = (typeof BODY_ZONES)[number];

export const BODY_MAP_CATEGORIES = ['pain', 'soreness', 'mobility', 'condition'] as const;
export type BodyMapCategory = (typeof BODY_MAP_CATEGORIES)[number];

export const TRIAGE_STATUSES = ['active', 'monitoring', 'cleared'] as const;
export type TriageStatus = (typeof TRIAGE_STATUSES)[number];

export const BODY_ZONE_LABELS: Record<BodyZoneId, string> = {
  chest_l: "Left Chest",
  chest_r: "Right Chest",
  obliques_l: "Left Obliques",
  obliques_r: "Right Obliques",
  abs_l: "Left Abs",
  abs_r: "Right Abs",
  biceps_l: "Left Biceps",
  biceps_r: "Right Biceps",
  triceps_l: "Left Triceps",
  triceps_r: "Right Triceps",
  neck: "Neck",
  trapezius_l: "Left Trapezius",
  trapezius_r: "Right Trapezius",
  deltoids_l: "Left Deltoid",
  deltoids_r: "Right Deltoid",
  adductors_l: "Left Adductors",
  adductors_r: "Right Adductors",
  quadriceps_l: "Left Quadriceps",
  quadriceps_r: "Right Quadriceps",
  knees_l: "Left Knee",
  knees_r: "Right Knee",
  tibialis_l: "Left Shin",
  tibialis_r: "Right Shin",
  calves_l: "Left Calf",
  calves_r: "Right Calf",
  forearm_l: "Left Forearm",
  forearm_r: "Right Forearm",
  hands_l: "Left Hand",
  hands_r: "Right Hand",
  ankles_l: "Left Ankle",
  ankles_r: "Right Ankle",
  feet_l: "Left Foot",
  feet_r: "Right Foot",
  head: "Head",
  upper_back_l: "Left Upper back",
  upper_back_r: "Right Upper back",
  lower_back_l: "Left Lower back",
  lower_back_r: "Right Lower back",
  gluteal_l: "Left Glute",
  gluteal_r: "Right Glute",
  hamstring_l: "Left Hamstring",
  hamstring_r: "Right Hamstring",
};

// Category display: label + chip/dot color. Colors read well on the dark theme.
export const CATEGORY_META: Record<BodyMapCategory, { label: string; short: string; color: string }> = {
  pain: { label: 'Pain', short: 'Pain', color: '#ef5350' },
  soreness: { label: 'Soreness', short: 'Sore', color: '#e2b84f' },
  mobility: { label: 'Mobility limitation', short: 'Mobility', color: '#5b9bd5' },
  condition: { label: 'Pre-existing condition', short: 'Condition', color: '#a3a3a3' },
};

// Display/grouping order — most clinically urgent first.
export const CATEGORY_ORDER: BodyMapCategory[] = ['pain', 'mobility', 'condition', 'soreness'];

export const TRIAGE_META: Record<TriageStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: '#ef5350' },
  monitoring: { label: 'Monitoring', color: '#e2b84f' },
  cleared: { label: 'Cleared', color: '#24c26a' },
};

export const zoneLabel = (id: string): string =>
  BODY_ZONE_LABELS[id as BodyZoneId] ?? id;
